import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Gift,
  Clock,
  Sparkles,
  MessageCircle,
  Heart,
  ArrowRight,
  Check,
} from "lucide-react";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

// ---------- Voucher data ----------
const TEMPO_PER_VOI = [
  {
    id: "piccola-pausa",
    name: "Piccola Pausa",
    hours: "2 ore",
    desc: "2 ore consecutive di cura educativa a domicilio.",
    price: "50 €",
    highlight: false,
  },
  {
    id: "una-serata",
    name: "Una Serata per Voi",
    hours: "3 ore",
    desc: "3 ore consecutive di cura educativa a domicilio.",
    price: "75 €",
    highlight: false,
  },
  {
    id: "tempo-prezioso",
    name: "Tempo Prezioso",
    hours: "4 ore",
    desc: "4 ore consecutive di cura educativa a domicilio.",
    price: "100 €",
    highlight: true,
  },
  {
    id: "pacchetto-relax",
    name: "Pacchetto Relax",
    hours: "6 ore",
    desc: "6 ore complessive di cura educativa a domicilio. Utilizzabili secondo le modalità concordate.",
    price: "150 €",
    highlight: false,
  },
];

const CRESCERE_CON_CURA = [
  {
    id: "una-consulenza",
    name: "Una Consulenza per Voi",
    hours: "1 consulenza",
    desc: "1 consulenza personalizzata sulla vostra realtà familiare.",
    price: "120 €",
    highlight: false,
  },
  {
    id: "percorso-essenziale",
    name: "Percorso Essenziale",
    hours: "3 consulenze",
    desc: "3 consulenze personalizzate — per approfondire e accompagnare un tema.",
    price: "250 €",
    highlight: true,
  },
  {
    id: "crescere-insieme",
    name: "Percorso Crescere Insieme",
    hours: "5 consulenze",
    desc: "5 consulenze personalizzate — un vero cammino insieme, nel tempo.",
    price: "370 €",
    highlight: false,
  },
];

const TEMI_CONSULENZA = [
  "Sonno e addormentamento",
  "Alimentazione complementare",
  "Autonomie",
  "Organizzazione degli spazi secondo Montessori",
  "Abbandono rispettoso del ciuccio",
  "Passaggio dal pannolino",
  "Sviluppo del gioco",
  "Routine quotidiane",
  "Inserimento al nido",
  "Sostegno educativo ai genitori",
];

const HOW = [
  { n: "01", title: "Scegli", desc: "Scegli il regalo che desideri donare tra le due linee." },
  { n: "02", title: "Personalizza", desc: "Personalizzalo con il nome del destinatario e una tua dedica." },
  { n: "03", title: "Ricevi", desc: "Ricevi il regalo in formato digitale, pronto da stampare o inviare." },
  { n: "04", title: "Prenota", desc: "La famiglia mi contatterà per prenotare l'incontro quando vorrà." },
];

const INFO = [
  "I regali di cura sono nominativi.",
  "Hanno validità di 12 mesi dalla data di acquisto.",
  "Gli appuntamenti sono soggetti alla disponibilità dell'agenda.",
  "I regali \"Tempo per Voi\" comprendono interventi occasionali per un bambino.",
  "Per richieste con più bambini, orari notturni, giorni festivi, spostamenti fuori zona o esigenze particolari, potrà essere richiesto un adeguamento della tariffa.",
  "I regali di cura non sono convertibili in denaro.",
];

const waLink = (voucher) => {
  const msg = encodeURIComponent(
    `Ciao Teresa, vorrei info sul regalo di cura "${voucher.name}". Puoi mandarmi tutti i dettagli? 🎁`
  );
  return `${BRAND.whatsappUrl}?text=${msg}`;
};

const waGenericLink = () => {
  const msg = encodeURIComponent(
    "Ciao Teresa, vorrei info sui Regali di Cura 🎁"
  );
  return `${BRAND.whatsappUrl}?text=${msg}`;
};

// ============================================================
// PAGE
// ============================================================
export default function Voucher() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], ["0%", "80%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 25]);

  return (
    <div className="pt-24 md:pt-28" data-testid="voucher-page">
      {/* ------------- HERO ------------- */}
      <section
        ref={heroRef}
        className="relative min-h-[70vh] px-6 md:px-10 py-16 md:py-28 overflow-hidden bg-[#FAF8F5]"
      >
        <div className="max-w-[1500px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-6">— Regali di Cura</p>
          <h1
            className="font-display text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.85] text-[#3D2140] mb-8"
            data-testid="voucher-heading"
          >
            <RevealText text={["Regala tempo."]} delay={0.1} />
            <RevealText text={["Regala cura."]} className="text-[#81C3C9] block" delay={0.2} />
            <RevealText text={["Regala serenità."]} className="text-[#FF2768] block" delay={0.3} />
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mt-10 md:mt-16">
            <div className="md:col-span-7 space-y-5 text-[#3D2140]/80 text-base md:text-lg leading-relaxed">
              <p>
                Ci sono regali che durano un giorno e altri che lasciano un ricordo nel cuore.
              </p>
              <p>
                I <em className="text-[#FF2768] not-italic font-semibold">Regali di Cura di Teresa Vilardo</em> nascono con un'idea semplice: offrire alle famiglie qualcosa di davvero prezioso.
              </p>
              <p className="font-display text-3xl md:text-4xl text-[#3D2140] leading-tight">Tempo.</p>
              <p>
                Tempo per riposare, per uscire insieme, per dedicarsi a sé stessi, per affrontare con maggiore serenità i primi anni di vita di un bambino. Oppure il tempo per ricevere il supporto di una professionista che accompagni i genitori in uno dei momenti più delicati della crescita.
              </p>
              <p className="italic font-editorial text-lg md:text-xl text-[#3D2140]">
                Perché prendersi cura dei genitori significa prendersi cura anche dei loro bambini.
              </p>
            </div>
            <div className="md:col-span-5 flex md:justify-end">
              <img
                src={BRAND.photos.joyfulFloral}
                alt="Teresa Vilardo"
                className="w-full max-w-md aspect-[4/5] object-cover rounded-[36px] shadow-2xl border-4 border-white"
                data-testid="voucher-hero-photo"
              />
            </div>
          </div>

          <motion.img
            src={BRAND.logo}
            alt=""
            aria-hidden
            style={{ y: bearY, rotate: bearRotate }}
            className="hidden lg:block absolute right-4 top-4 h-24 w-24 rounded-full opacity-60 animate-floaty drop-shadow-2xl pointer-events-none"
          />
        </div>
      </section>

      {/* ------------- OCCASIONI ------------- */}
      <section className="px-6 md:px-10 py-16 md:py-20 max-w-[1500px] mx-auto">
        <div className="rounded-3xl bg-[#81C3C9]/15 p-8 md:p-12 border-l-4 border-[#FF2768]">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Occasioni</p>
          <p className="text-[#3D2140] text-base md:text-lg leading-relaxed max-w-4xl">
            I regali di cura possono essere donati in occasione di una <strong>nascita</strong>, di un <strong>compleanno</strong>, del <strong>Natale</strong>, della <strong>Festa della Mamma</strong>, della <strong>Festa del Papà</strong> o semplicemente come gesto di affetto verso una famiglia. Sono il pensiero perfetto da parte del <em>partner, dei nonni, degli zii, degli amici o dei colleghi</em>.
          </p>
        </div>
      </section>

      {/* ------------- VOUCHER: TEMPO PER VOI ------------- */}
      <section className="px-6 md:px-10 py-16 md:py-24 max-w-[1500px] mx-auto" data-testid="voucher-tempo">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12">
          <div className="md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Linea 01</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9] mb-6">
              Tempo<br/><span className="text-[#FF2768]">per Voi.</span>
            </h2>
            <p className="text-[#3D2140]/75 text-base md:text-lg leading-relaxed max-w-2xl">
              Un aiuto concreto per regalare ai genitori qualche ora di tranquillità, sapendo che il proprio bambino sarà seguito da un'educatrice professionista specializzata nella <strong>fascia 0-36 mesi</strong>. Ogni incontro è costruito nel rispetto dei tempi, dei bisogni e delle abitudini della famiglia.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TEMPO_PER_VOI.map((v, i) => (
            <VoucherCard key={v.id} voucher={v} index={i} theme="pink" />
          ))}
        </div>
      </section>

      {/* ------------- VOUCHER: CRESCERE CON CURA ------------- */}
      <section className="px-6 md:px-10 py-16 md:py-24 bg-[#3D2140] text-white" data-testid="voucher-crescere">
        <div className="max-w-[1500px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12">
            <div className="md:col-span-7">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Linea 02</p>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.9] mb-6">
                Crescere<br/><span className="text-[#81C3C9]">con Cura.</span>
              </h2>
              <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
                Un regalo pensato per quei genitori che desiderano essere accompagnati da una professionista nei primi anni di vita del proprio bambino. Ogni consulenza è personalizzata e costruita sulla realtà della famiglia.
              </p>
            </div>
          </div>

          {/* Temi di consulenza */}
          <div className="mb-10 md:mb-14">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#81C3C9] mb-4">Può riguardare, ad esempio</p>
            <div className="flex flex-wrap gap-2">
              {TEMI_CONSULENZA.map((t) => (
                <span key={t} className="text-sm bg-white/8 border border-white/12 rounded-full px-4 py-2 text-white/85">
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {CRESCERE_CON_CURA.map((v, i) => (
              <VoucherCard key={v.id} voucher={v} index={i} theme="dark" />
            ))}
          </div>
        </div>
      </section>

      {/* ------------- COME FUNZIONA ------------- */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1500px] mx-auto" data-testid="voucher-how">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12 md:mb-16">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Come funziona</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              Quattro passi,<br/><span className="text-[#81C3C9]">semplici.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {HOW.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-[#FAF8F5] rounded-3xl p-8 border border-[#3D2140]/5 hover:border-[#FF2768]/30 transition-colors"
            >
              <span className="font-display text-5xl text-[#FF2768] leading-none">{s.n}</span>
              <h3 className="font-display text-3xl text-[#3D2140] mt-4 mb-3">{s.title}</h3>
              <p className="text-sm text-[#3D2140]/70 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ------------- INFO UTILI ------------- */}
      <section className="px-6 md:px-10 py-16 md:py-20 bg-[#FAF8F5]" data-testid="voucher-info">
        <div className="max-w-[1100px] mx-auto">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Informazioni utili</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#3D2140] leading-[0.95] mb-10">Cose da sapere,<br/><span className="text-[#81C3C9]">senza sorprese.</span></h2>
          <ul className="space-y-4">
            {INFO.map((info, i) => (
              <li key={i} className="flex items-start gap-4 bg-white rounded-2xl p-5 border border-[#3D2140]/8">
                <Check size={18} className="text-[#FF2768] flex-shrink-0 mt-1" />
                <span className="text-[#3D2140]/85 leading-relaxed">{info}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ------------- PERCHÉ SCEGLIERE ------------- */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1300px] mx-auto text-center" data-testid="voucher-why">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-6">— Perché sceglierlo</p>
        <h2 className="font-display text-5xl md:text-7xl lg:text-8xl text-[#3D2140] leading-[0.9] mb-10">
          Il regalo più bello,<br/><span className="text-[#FF2768]">quando nasce un bambino.</span>
        </h2>
        <div className="space-y-4 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto text-[#3D2140]/80">
          <p>Non è un altro oggetto.</p>
          <p>È <strong>qualcuno che si prende cura della famiglia.</strong></p>
          <p>È qualche <em>ora di riposo</em>.</p>
          <p>È <em>una cena insieme</em>.</p>
          <p>È <em>un confronto professionale</em>.</p>
          <p>È <em>sentirsi meno soli</em>.</p>
          <p className="pt-4 font-editorial italic text-xl md:text-2xl text-[#3D2140]">
            È sapere che, anche per un momento, qualcuno si prenderà cura di ciò che abbiamo di più prezioso.
          </p>
        </div>
      </section>

      {/* ------------- CTA WHATSAPP ------------- */}
      <section className="px-6 md:px-10 py-16 md:py-24" data-testid="voucher-cta">
        <div className="max-w-[1200px] mx-auto">
          <div className="relative overflow-hidden rounded-[32px] md:rounded-[48px] bg-gradient-to-br from-[#FF2768] to-[#E01F5A] p-8 md:p-14 lg:p-20 shadow-2xl">
            <img
              src={BRAND.logo}
              alt=""
              aria-hidden
              className="absolute -right-16 -top-16 md:-right-20 md:-top-20 h-64 w-64 md:h-96 md:w-96 opacity-15 rotate-12 pointer-events-none"
            />
            <div className="absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-[#81C3C9]/30 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
              <div className="md:col-span-8">
                <p className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-white/90 mb-5 font-semibold">Richiedi il tuo regalo</p>
                <h3 className="font-display text-4xl md:text-6xl lg:text-7xl text-white leading-[0.9] mb-4">
                  Regalalo oggi.<br/>Sarà per sempre.
                </h3>
                <p className="text-white/90 text-base md:text-lg leading-relaxed max-w-xl">
                  Scrivimi su WhatsApp: personalizziamo insieme la dedica e ti mando il regalo in formato digitale pronto da donare 🎁
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <a
                  href={waGenericLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="voucher-whatsapp-cta"
                  className="group inline-flex items-center gap-3 bg-white text-[#FF2768] rounded-full px-8 md:px-10 py-4 md:py-5 font-semibold text-base md:text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                >
                  <MessageCircle size={20} />
                  Richiedi su WhatsApp
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------- Voucher Card ----------
function VoucherCard({ voucher, index, theme = "pink" }) {
  const isDark = theme === "dark";
  const cardBg = isDark ? "bg-[#3D2140] border-white/10" : "bg-white border-[#3D2140]/8";
  const highlightBorder = voucher.highlight
    ? isDark
      ? "ring-2 ring-[#FF2768]"
      : "ring-2 ring-[#FF2768]"
    : "";
  const nameColor = isDark ? "text-white" : "text-[#3D2140]";
  const descColor = isDark ? "text-white/75" : "text-[#3D2140]/70";
  const hoursColor = isDark ? "text-[#81C3C9]" : "text-[#81C3C9]";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className={`relative rounded-3xl p-7 md:p-8 border ${cardBg} ${highlightBorder} hover:border-[#FF2768]/50 transition-all flex flex-col`}
      data-testid={`voucher-card-${voucher.id}`}
    >
      {voucher.highlight && (
        <span className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.3em] bg-[#FF2768] text-white px-3 py-1 rounded-full font-semibold shadow-lg">
          Più scelto
        </span>
      )}

      <div className="flex items-center gap-2 mb-3">
        <Gift size={16} className="text-[#FF2768]" />
        <span className={`text-[10px] uppercase tracking-[0.3em] font-semibold ${hoursColor}`}>{voucher.hours}</span>
      </div>

      <h3 className={`font-display text-3xl md:text-4xl leading-tight mb-3 ${nameColor}`}>{voucher.name}</h3>
      <p className={`text-sm leading-relaxed mb-6 flex-1 ${descColor}`}>{voucher.desc}</p>

      <a
        href={waLink(voucher)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`voucher-btn-${voucher.id}`}
        className={`inline-flex items-center justify-center gap-2 mt-auto w-full px-5 py-3 rounded-full text-sm font-semibold uppercase tracking-wider transition-all ${
          isDark
            ? "bg-white/10 text-white border border-white/20 hover:bg-[#FF2768] hover:border-[#FF2768]"
            : "bg-transparent text-[#3D2140] border border-[#3D2140]/25 hover:bg-[#FF2768] hover:text-white hover:border-[#FF2768]"
        }`}
      >
        Contattami per saperne di più
        <ArrowRight size={14} />
      </a>
    </motion.div>
  );
}
